/**
 * React context + status hook for the org's activity socket (PX-W6.1, ported
 * from xelerate-ai's `providers/webSocketContext.ts`; TASK-7922504E added the
 * `socket` itself so `useActivityContext` can hold contextIds on it).
 *
 * Split out from `WebSocketProvider.tsx` so the provider file only exports a
 * component (react-refresh/only-export-components). This module owns no
 * connection logic — it is pure context plumbing.
 */

import { createContext, useContext } from 'react'
import type { ActivitySocket, ConnectionStatus } from '@/services/websocket'

export interface WebSocketContextValue {
  /** Whether the org's activity socket is established. */
  isConnected: boolean
  /** Current connection status of the org's activity socket. */
  status: ConnectionStatus
  /** The org's one activity socket; `null` while no org is active. */
  socket: ActivitySocket | null
}

export const WebSocketContext = createContext<WebSocketContextValue>({
  isConnected: false,
  status: 'disconnected',
  socket: null
})

/**
 * Access the org's activity socket and its connection status.
 *
 * @returns The current connection status, a derived `isConnected` flag and the socket.
 */
export function useWebSocketStatus(): WebSocketContextValue {
  return useContext(WebSocketContext)
}
